/**
 * useOKRPermissions Hook
 *
 * Calculates role-based permissions for OKR dashboard actions
 */

import { useMemo } from 'react';
import { UserContext, OKRPermissions } from '../types';
import { isUserAdmin, isUserHR, isUserManager } from '../utils/filterUtils';

export interface UseOKRPermissionsReturn extends OKRPermissions {}

export const useOKRPermissions = (
    userContext: UserContext,
    isLocked: boolean
): UseOKRPermissionsReturn => {
    const permissions = useMemo(() => {
        const isAdmin = isUserAdmin(userContext);
        const isHR = isUserHR(userContext);
        const isManager = isUserManager(userContext);

        return {
            // Role flags for conditional rendering
            isAdmin,
            isManager,
            isHR,

            // Org OKR
            canCreateOrgOKR: isAdmin,
            canEditOrgOKR: isAdmin && !isLocked,
            canLockOrgOKR: isAdmin,
            canViewAllTeams: isAdmin || isHR,

            // Team OKR - Manager can always edit their team
            canCreateTeamOKR: isManager,
            canEditTeamOKR: isManager,
            canViewDeptMembers: isManager || isAdmin || isHR,

            // Individual OKR - only managers and admins can create objectives
            canCreateIndividualOKR: isAdmin || isManager,
            canEditMilestones: !isAdmin || isManager,

            // Teams/Departments
            canCreateTeam: isAdmin,
            canManageUsers: isAdmin,

            // Notes - everyone can view, only admin/manager can add
            canViewNotes: true,
            canAddNotes: isAdmin || isManager,

            // Admin-only controls
            canEditStatus: isAdmin,
            canDeleteObjectives: isAdmin,
            canReassignObjectives: isAdmin,
            canViewAllObjectives: isAdmin || isHR,

            // Manager-specific
            canAddFeedback: isManager,
            canAssignMilestones: isManager,
        };
    }, [userContext, isLocked]);

    return permissions;
};

export default useOKRPermissions;
